import dayjs, { Dayjs } from "dayjs";
import { Experience, AgeBand } from "../../../../context/dataExperienceContext";
import { useCurrency } from "../../../../context/currencyContext";
import { Price, PricingDetail, PricingRecord, BookableItem } from "../../../../context/viator-product-types";
import { TravelersState, useTravelers } from "../../../../context/travelersContext";
import useExperienceData from "../../../../hooks/useExperienceData";

// Helper function to check if a date is within a season range
export const isDateWithinSeason = (date: Dayjs, seasonStart: Dayjs, seasonEnd: Dayjs): boolean => {
  return (date.isAfter(seasonStart, 'day') || date.isSame(seasonStart, 'day')) &&
    (date.isBefore(seasonEnd, 'day') || date.isSame(seasonEnd, 'day'));
};

// Function to get the lowest price for a date from pricing details.
// Keep in mind that these bookableItems were already filtered by getAvailableBookableItems meaning that
// unavailableDates and dayOfWeek were already checked.
const getBookableItemsLowestPrice = (date: Dayjs, bookableItems: Experience['availabilitySchedule']['bookableItems']): number | undefined => {
  let lowestPrice: number | undefined;

  bookableItems.forEach(item => {
    item.seasons.forEach(season => {
      const seasonStart = dayjs(season.startDate);
      const seasonEnd = season.endDate ? dayjs(season.endDate) : dayjs().add(1, 'year');

      if (isDateWithinSeason(date, seasonStart, seasonEnd)) {
        season.pricingRecords.forEach(record => {
          const ageBands: AgeBand[] = ['ADULT', 'SENIOR', 'YOUTH', 'CHILD', 'INFANT', 'TRAVELER'];

          for (const ageBand of ageBands) {
            const pricingDetail = record.pricingDetails.find(pd => pd.ageBand === ageBand);
            if (pricingDetail) {
              const price = pricingDetail.price.original.recommendedRetailPrice;
              if (!lowestPrice || price < lowestPrice) {
                lowestPrice = price;
              }
              break;
            }
          }
        });
      }
    });
  });

  return lowestPrice;
};


export const useDateAvailabilityAndConvert = (date: Dayjs, availabilitySchedule: Experience['availabilitySchedule']):
  ({ isAvailable: true; price: number, code: string } |
  { isAvailable: false }) => {
  const { experience } = useExperienceData();
  const { convert } = useCurrency();
  // FIXED AMOUNT IN ARS.
  const promoterCommission = experience?.price.promoter_commission || 0
  const availability = getDateAvailability(date, availabilitySchedule);
  if (availability.isAvailable) {
    const res = convert(availability.price, availabilitySchedule.currency);
    return {
      isAvailable: availability.isAvailable,
      price: res.value + promoterCommission,
      code: res.code,
    }

  } else return { isAvailable: availability.isAvailable };
}

// Function to check if a date is available and get its price
export const getDateAvailability = (date: Dayjs, availabilitySchedule: Experience['availabilitySchedule']): (
  { isAvailable: true, price: number } |
  { isAvailable: false }
) => {
  const dateStr = date.format('YYYY-MM-DD');
  const availableBookableItems = getAvailableBookableItems(dateStr, availabilitySchedule);

  // There are not bookable items for the date date.
  if (availableBookableItems.length === 0) {
    return { isAvailable: false };
  }

  const lowestPrice = getBookableItemsLowestPrice(date, availableBookableItems);
  // this should never happen since we are identifiing the lowest price in the availableBookableItems collection.
  if (!lowestPrice) {
    return { isAvailable: false };
  }
  return {
    isAvailable: true,
    price: lowestPrice
  };
};

/**
 * This function returns Available bookable items without taking into consideration travelers.
 * bue even some option might be available per date it might be not suitable for the travelers selection.
 * This because a e.g BookableItem#pricingDetails['ADULT'] may define a min amount of travelers for this option.
 */ 
export const getAvailableBookableItems = (date: string, availabilitySchedule: Experience['availabilitySchedule']): Experience['availabilitySchedule']['bookableItems'] => {
  if (!availabilitySchedule?.bookableItems) return [];

  const selectedDayjs = dayjs(date);
  const dayOfWeek = selectedDayjs.format('dddd').toUpperCase();

  return availabilitySchedule.bookableItems.filter(item => {
    return item.seasons.some(season => {
      const seasonStart = dayjs(season.startDate);
      const seasonEnd = season.endDate ? dayjs(season.endDate) : dayjs().add(1, 'year');

      const isWithinSeason = isDateWithinSeason(selectedDayjs, seasonStart, seasonEnd)

      // Check if day is available in pricing records
      const isDayAvailable = season.pricingRecords.some(record =>
        record.daysOfWeek.includes(dayOfWeek) &&
        !record.unavailableDates?.some(ud => ud.date === date) // Verify this is properly captured.
      );

      return isWithinSeason && isDayAvailable;
    });
  });
};

// IMPORTANTE partnerTotalPrice viene por API si sos partner.
export const getPriceForSelectedDate = (price: Price, selectedDate: string) => {
  if (price.special) {
    const offerStart = new Date(price.special.offerStartDate);
    const offerEnd = new Date(price.special.offerEndDate);
    const selected = new Date(selectedDate);

    if (selected >= offerStart && selected <= offerEnd) {
      return price.special.recommendedRetailPrice;
    }
  }
  return price.original.recommendedRetailPrice;
}
export const getPriceForAgeBandAndSelectedDate = (pricingRecord: PricingRecord, ageBand: string, selectedDate: string) => {
  const pricingDetail = pricingRecord.pricingDetails.find(pd => pd.ageBand === ageBand) as PricingDetail;
  return getPriceForSelectedDate(pricingDetail.price, selectedDate)
};

export interface PriceBreakdownEntry {
  ageBand: string;
  count: number;
  price: number;
}
/**
 * Pricing detail depends on the AgeBand and also may depend on the amount of travelers for that ageBand.
 * This technice is used to segment pricing based on travelers count:
 *  Adults: from 0 to 3 each adult costs 100$.
 * Adults: from 4 to 5 each adult costs 70$.
 */
const findPricingDetail = (pricingRecord: PricingRecord, ageBand: string, count: number) => {
  return pricingRecord.pricingDetails.find(pd => {
    if (pd?.minTravelers !== undefined && pd.maxTravelers !== undefined) {
      return pd.ageBand === ageBand && pd.minTravelers <= count && count <= pd.maxTravelers;
    } else
      return pd.ageBand === ageBand;
  });
};

export const calculateTotalAmountBreakdown = (
  travelers: TravelersState,
  currentPricingRecord: PricingRecord,
  selectedDate: string
): PriceBreakdownEntry[] => {
  return Object.entries(travelers)
    .filter(([_, count]) => count > 0)
    .map(([ageBand, count]) => {
      const pricingDetail = findPricingDetail(currentPricingRecord, ageBand, count);

      const pricePerPerson = pricingDetail
        ? getPriceForSelectedDate(pricingDetail.price, selectedDate)
        : 0;

      return {
        ageBand,
        count,
        price: pricePerPerson,
      };
    });
};

export const calculateTotalAmount = (
  travelers: TravelersState,
  currentPricingRecord: PricingRecord,
  selectedDate: string
) => {

  const totalPrice = Object.entries(travelers).reduce((total, [ageBand, count]) => {
    const pricingDetail = findPricingDetail(currentPricingRecord, ageBand, count);
    if (pricingDetail && count > 0) {
      if(pricingDetail.pricingPackageType === 'UNIT') {
        const groupSize = pricingDetail.maxTravelers || 1;
        const numberOfGroups = Math.ceil(count / groupSize);
        return total + (getPriceForSelectedDate(pricingDetail.price, selectedDate) * numberOfGroups);
      } else {
        return total + (getPriceForSelectedDate(pricingDetail.price, selectedDate) * count);
      }
    }
    return total;
  }, 0);
  return totalPrice
}

// If the function returns undefined means that there is not suitable Pricing record for the selected BookableItem, date and travelers selection.
export const getBookableItemPricingRecord = (item: BookableItem, selectedDate: string, travelers: TravelersState): PricingRecord | undefined => {
  const currentSeason = item.seasons.find(season => {
    const seasonStart = new Date(season.startDate);
    const seasonEnd = season.endDate ? new Date(season.endDate) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    const selectedDateObj = new Date(selectedDate);
    return selectedDateObj >= seasonStart && selectedDateObj <= seasonEnd;
  });


  const satisfyTravelersRestriction = (record: PricingRecord, travelers: TravelersState): boolean => {
    const satisfyRestriccions = Object.entries(travelers).map(([ageBand, count]) => {
      const pricingDetail = record.pricingDetails.find(detail => detail.ageBand === ageBand);
      if(pricingDetail) {
        const { minTravelers, maxTravelers } = pricingDetail
        if(minTravelers !== undefined && maxTravelers !== undefined) {
          const satisfyPricingDetailTravelersRestriction = minTravelers <= count && maxTravelers >= count;
          return satisfyPricingDetailTravelersRestriction;
        }
      }
      
      return true;
    })
    return satisfyRestriccions.every(entry => entry === true);
  
  };
  
  const dayOfWeek = new Date(selectedDate + 'T00:00:00').toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
  return currentSeason?.pricingRecords.find(record => {
    return (
      record.daysOfWeek.includes(dayOfWeek) && (satisfyTravelersRestriction(record, travelers))
    )
  }
  );
};

export const useSelectedBookableItem = () => {
  const { experience } = useExperienceData();
  const { selectedItemCode } = useTravelers();
  const bookableItem = experience?.availabilitySchedule.bookableItems.find(option => option.productOptionCode === selectedItemCode);
  return bookableItem;
}

export const useSelectedItemTotalAmount = () => {
  const bookableItem = useSelectedBookableItem();
  return useTotalAmountForItem(bookableItem);
}

const countTravelersToCalculateComission = (travelers: TravelersState): number => {
  const total = Object.entries(travelers).reduce((total, [_, count]) => {
    // Shall we skip Babies if these are free of charge ?
    return total + count;
  }, 0)
  return total;
}

export const useTotalAmountForItem = (item?: BookableItem) => {
  const { experience } = useExperienceData();
  const { travelers, selectedDate } = useTravelers();
  if(!item) return null;
  const currentPricingRecord = getBookableItemPricingRecord(item, selectedDate, travelers);
  const productCurrency = experience?.availabilitySchedule.currency
  if(currentPricingRecord === undefined || productCurrency === undefined) return null;
  const totalAmount = calculateTotalAmount(travelers, currentPricingRecord, selectedDate);
  const entries = calculateTotalAmountBreakdown(travelers, currentPricingRecord, selectedDate);

  const countForComission = countTravelersToCalculateComission(travelers);


  return { totalAmount, productCurrency, totalBreakdown: entries, countForComission } 
}